<script lang="ts" setup>
import useMainStore from "~/shared/store";
import useSearchStore from "~/shared/store/modules/search";
import AppButton from "~/components/ui/buttons/AppButton.vue";
import ArrowIcon from "~/assets/icons/arrows/24/arrow-left.svg?component"
import type {IImageSlide} from "~/shared/interfaces/ui/sliders";
import type {Swiper} from "swiper/types";

defineProps<{
  slides: IImageSlide[]
}>()

const router = useRouter()
const mainStore = useMainStore()
const searchStore = useSearchStore()
const mainSlider = ref<Swiper>()
const thumbSlider = ref<Swiper>()
function goToPreviousPage() {
  if (router.options.history.state.back) {
    return router.go(-1);
  }
  return router.push('/');
}

function setMainSwiper(swiper: Swiper) {
  mainSlider.value = swiper;
}

function setThumbsSwiper(swiper: Swiper) {
  thumbSlider.value = swiper;
}
function showFSSlider(slides: IImageSlide[]) {
  searchStore.setSearchProductSlides(slides);
  mainStore.toggleFSSlider(true);
}
</script>

<template>
  <div
    class="product-slider"
  >
    <AppButton
      class="product-slider__back-btn"
      type="icon"
      sub-type="overlay"
      :icon="ArrowIcon"
      :is-icon-button-rounded="true"
      size="s"
      @click="goToPreviousPage"
    />
    <Swiper
      class="main-slider"
      ref="mainSlider"
      :modules="[SwiperNavigation, SwiperThumbs, SwiperController, SwiperPagination]"
      :pagination="{clickable: true}"
      :loop="true"
      :slidesPerView=1
      :watchSlidesProgress="true"
      :navigation="{
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next',
      }"
      @swiper="setMainSwiper"
      :thumbs="{ swiper: thumbSlider}"
      :class="slides.length === 1 && 'one-slide'"
    >
      <SwiperSlide
        v-for="slide in slides"
        :key="`fsMainSlider_${slide.url}`"
        @click="showFSSlider(slides)"
      >
        <div class="swiper-slide__image">
          <nuxt-img
            :src="slide.url"
            :alt="slide.name ? `${slide.name} - купить в интернет-магазине Parts.Sotrans` : 'Изображение товара Parts.Sotrans'"
            loading="lazy"
          />
        </div>
      </SwiperSlide>
    </Swiper>
    <Swiper
      v-if="slides.length > 1"
      ref="thumbSlider"
      class="thumb-slider"
      :modules="[SwiperNavigation, SwiperThumbs, SwiperController]"
      :watchSlidesProgress="true"
      :loop="false"
      :breakpoints="{
        320: {
        slidesPerView: 'auto',
        spaceBetween: 8,
        },
        1025: {
          slidesPerView: 5,
          spaceBetween: 8,
        }
      }"
      @swiper="setThumbsSwiper"
    >
      <SwiperSlide
        v-for="slide in slides"
        :key="`thumbSlider_${slide.url}`"
      >
        <nuxt-img
          :src="slide.url"
          :alt="slide.name ? `${slide.name} - купить в интернет-магазине Parts.Sotrans` : 'Изображение товара Parts.Sotrans'"
          loading="lazy"
        />
      </SwiperSlide>
    </Swiper>
  </div>
</template>

<style lang="scss" scoped>
.product-slider {
  width: 432px;
  @include md-breakpoint {
    width: 100%;
    position: relative;
  }

  &__back-btn {
    display: none;
    @include md-breakpoint {
      display: flex;
      position: absolute;
      top: 12px;
      left: 20px;
      z-index: 5;
    }
  }

  .main-slider {
    height: 432px;
    @include md-breakpoint {
      padding-bottom: 24px;
      height: 384px;
    }

    .swiper {
      &-slide {
        position: relative;
        cursor: zoom-in;

        @include md-breakpoint {
          display: flex;
          align-items: center;
          justify-content: center;

          img {
            max-width: 360px;
          }
        }

        &:active {
          cursor: grabbing;
        }

        &:after {
          content: '';
          display: block;
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          background-color: #6262620D;
          pointer-events: none;
        }

        &__image {
          height: 100%;
          width: 100%;
          @include md-breakpoint {
            display: flex;
            align-items: center;
            justify-content: center;
          }

          img {
            width: 100%;
            height: 100%;
            object-position: center;
            object-fit: cover;
          }
        }
      }

    }
    :deep(.swiper-pagination) {
      display: none;
      @include md-breakpoint {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 4px;
        bottom: 0;
        height: 24px;
      }

      .swiper-pagination-bullet {
        margin: 0;
        width: 6px;
        height: 6px;
        background-color: $footer_control-bg-grey;
        transition: background-color .3s, width .3s, border-radius .3s;

        &-active {
          background-color: $control-bg_black;
          width: 16px;
          border-radius: 4px;
        }
      }
    }
  }
  .thumb-slider {
    height: 80px;
    margin-top: 16px;
    @include md-breakpoint {
      display: none;
    }

    .swiper {
      &-slide {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 16px;
        cursor: pointer;
        transition: border-color .3s;
        border: 2px solid transparent;

        &:hover:not(:active, .swiper-slide-thumb-active) {
          @include mf-breakpoint {
            border-color: $control-content_tretiary;
          }
        }

        &-thumb {
          &-active {
            border-color: $content_orange;
          }
        }

        img {
          width: 72px;
          height: 72px;
          object-position: center;
          object-fit: cover;
          border-radius: 12px;
        }
      }
    }
  }
}
</style>
